import React, { useEffect, useState, Suspense, lazy } from "react";
import { Helmet } from "react-helmet-async";
import Header from "../Header/index";
import localJsonData from "./trademarks.json";  
import "./MainPage.css";

// Lazy load components
const Banner = lazy(() => import("../Banner/Banner.js"));
const Body = lazy(() => import("../Body/index"));
const Footer = lazy(() => import("../Footer/index"));
 
export default function MainPage() {
  const cdnUrl = process.env.REACT_APP_CDN_PATH;
  const domain = process.env.REACT_APP_DOMAIN;
  const [showBelowFold, setShowBelowFold] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 991);
  const [jsonData, setJsonData] = useState(localJsonData); // Start with local data immediately

  // Fetch JSON from S3 in background
  useEffect(() => {
    const fetchJsonData = async () => {
      try {
        const timestamp = Date.now();
        const response = await fetch(`${cdnUrl}trademarks.json?timestamp=${timestamp}`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setJsonData(data); // Silently update when ready
      } catch (error) {
        console.warn("Failed to fetch JSON from S3, using local fallback:", error);
        // Already using localJsonData, so no action needed
      }
    };
    fetchJsonData();
  }, [cdnUrl]);

  // Remove the loading state and LoadingSpinner entirely!

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 991);
    };
    window.addEventListener("resize", handleResize);

    const handleTriggerBelowFold = () => {
      setShowBelowFold(true);
    };
    window.addEventListener("triggerBelowFold", handleTriggerBelowFold);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("triggerBelowFold", handleTriggerBelowFold);
    };
  }, []);

  // Meta tags configuration based on domain
  const getMetaTags = () => {
    if (domain === 'swmc') {
      return {
        title: "Registered Trademarks of Sun West Mortgage Company | Official Sun West Marks",
        description: "Discover the officially registered trademarks of Sun West Mortgage Company, showcasing Home of Fair Lending, Mortgage Possible, Tru Approval, and more—representing our commitment to fair lending, innovation, and AI-powered mortgage solutions.",
        keywords: "Sun West trademarks, Sun West Mortgage trademarks, Sun West registered marks, Home of Fair Lending trademark, Mortgage Possible trademark, Tru Approval trademark, Homebuyer Connect trademark, Sun West service marks, Sun West USPTO trademarks, Sun West branding, Sun West proprietary technology, Sun West mortgage solutions, fair lending trademarks, AI mortgage trademarks, TotalQM trademark, Loanfellas trademark, Buffy the Rate Slayer trademark, Rate Slayer trademark, Simple HECM trademark, Sun West intellectual property, Sun West legal trademarks, SWMC trademarks list",
        url: "swmc.com/trademarks",
        favicon: `${cdnUrl}swmc/favicon-swmc.ico`,
        thumbnail: `${cdnUrl}swmc/thumbnail-swmc.jpg`
      };
    } else {
      return {
        title: "Registered Trademarks of Celligence LLC | Official Celligence AI & Technology Marks",
        description: "Explore the registered trademarks of Celligence LLC, including AI You Can Bank On, Brillianeers, ANGL Token, and more—showcasing our AI-driven innovations and next-generation technologies powering intelligent automation and financial solutions.",
        keywords: "Celligence trademarks, Celligence registered marks, Celligence USPTO trademarks, AI You Can Bank On trademark, Brillianeers trademark, We Get You trademark, Be Limitless trademark, Nothing Is Beyond Reach trademark, Your True Home Is Always Within trademark, Celligence service marks, Celligence technology trademarks, AI mortgage trademarks, AI broker trademarks, ANGL Token trademark, Celligence intellectual property, Celligence branding, Celligence innovation marks, Celligence legal trademarks, fintech trademarks, AI automation trademarks",
        url: "celligence.com/trademarks",
        favicon: `${cdnUrl}celligence/favicon-celligence.ico`,
        thumbnail: `${cdnUrl}celligence/thumbnail-celligence.jpg`
      };
    }
  };

  const metaTags = getMetaTags();

  useEffect(() => {
    if (window.innerHeight > 1000) {
      setShowBelowFold(true);
      return;
    }
    const handleScroll = () => {
      setShowBelowFold(true);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true,
      once: true,
    });
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <div className="trademarks-page">
      <Helmet>
        <title>{metaTags.title}</title>
        <meta name="description" content={metaTags.description} />
        <meta name="keywords" content={metaTags.keywords} />
        <link rel="canonical" href={`https://${metaTags.url}`} />
        <link rel="icon" href={metaTags.favicon} />
        
        {/* Open Graph */}
        <meta property="og:url" content={`https://${metaTags.url}`} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={metaTags.title} />
        <meta property="og:description" content={metaTags.description} />
        <meta property="og:image" content={metaTags.thumbnail} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="600" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:url" content={`https://${metaTags.url}`} />
        <meta name="twitter:title" content={metaTags.title} />
        <meta name="twitter:description" content={metaTags.description} />
        <meta name="twitter:image" content={metaTags.thumbnail} />
        
        {/* Apple Touch Icon */}
        <link rel="apple-touch-icon" href={metaTags.thumbnail} />
      </Helmet>
      
      <Header />
      
      <Suspense fallback={<div style={{ minHeight: "300px" }} />}>
        <Banner data={jsonData} />
      </Suspense>
      
      <Suspense fallback={<div style={{ minHeight: "400px" }} />}>
        <Body data={jsonData} />
      </Suspense>
        
      {showBelowFold && (
        <Suspense fallback={<div style={{ minHeight: "200px" }} />}>
          <Footer />
        </Suspense>
      )}
    </div>
  );
}
